/**
 * PL ①シート版(社内ドラフト。2026-09-10 指示 → 2026-09-10 夜に「1日シートと全く同じUIで 日/週/月、横軸は会社別」で再作成)。
 * 現行「PL日報」の「1日」シートに寄せた表: 左に 大分類／中分類／項目 の3列、右に 会社列＋合計。
 * 行の並び・名前はシートのとおり(構成比 → アポ数 → 売上 → 売上原価 → 粗利 → 経費 → 営業利益 → 営業外 → 経常利益)。
 * 期間は画面上部の 日次／週次／月次(その期間の合計を1枚のシートとして出す)。見出しは固定、行クリックでアポインター別の内訳。
 * 2026-09-18 UIデザインシステムの集計表に置き換え(D-90): まとまりは色帯ではなく 大分類の列と区切り線で示し、小計・合計の行だけ薄い地にする。
 * 原価率は目標を超えた会社だけ赤字と印。
 */
import HelpTip, { type Help } from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import ToneValue from '@core/components/ui/ToneValue';
import type { Company } from '@core/lib/types';
import { HELP, costRateTone, costRateVerdict } from './colors';
import { fmtCellUnit, MONEY_UNIT_LABELS, TONE_LABELS, type MoneyUnit, type PlRow } from './plRow';

/** 区分の見出し行(「売上原価 ／ アポ報酬」)を 大分類・中分類 に分ける */
function splitSection(label: string): { cat1: string; cat2: string } {
  const parts = label.split('／').map((s) => s.trim());
  return { cat1: parts[0] ?? '', cat2: parts.slice(1).join(' / ') };
}

function helpOf(r: PlRow): Help | null {
  if (!r.hint) return null;
  return { title: r.label, what: r.hint, better: r.better ?? '—' };
}

export default function PlSheetView({
  rows,
  cols,
  open,
  toggle,
  costTarget,
  unit,
  compact = false,
}: {
  rows: PlRow[];
  cols: Company[];
  open: Set<string>;
  toggle: (k: string) => void;
  costTarget: number;
  /** 金額の単位(円／千円／万円。画面上部の切替) */
  unit: MoneyUnit;
  /** 行を詰める(36px) */
  compact?: boolean;
}) {
  /** 直前の見出し行から引き継ぐ 大分類・中分類 */
  let cat1 = '';
  let cat2 = '';

  const valueCells = (r: PlRow) => {
    const cell = (v: number | null, isTotal = false, key?: string) => {
      const text = fmtCellUnit(v, r.fmt, unit);
      const verdict = r.colorRate ? costRateVerdict(v, costTarget) : null;
      const shown = r.colorRate ? <ToneValue tone={costRateTone(v, costTarget)}>{text}</ToneValue> : text;
      return (
        <td key={key} className={`is-num${isTotal ? ' col-total' : ''}${v === null ? ' cell-na' : ''}`}>
          {verdict ? (
            <HelpTip help={HELP.costRate} verdict={verdict} value={text}>
              {shown}
            </HelpTip>
          ) : (
            shown
          )}
        </td>
      );
    };
    return (
      <>
        {cols.map((c) => cell(r.values[c.id] ?? null, false, c.id))}
        {cell(r.total, true)}
      </>
    );
  };

  // 1) まず行を並べる(見出し行は 大分類・中分類 に読み替えて消す。開いている行の下に内訳を入れる)
  //    大分類は科目のまとまり(tone)から決める。シートと同じく 粗利・営業利益・経常利益 もそれぞれ独立した帯になる
  type Line = { row: PlRow; cat1: string; cat2: string; child: boolean; summary: boolean };
  const lines: Line[] = [];
  for (const r of rows) {
    if (r.section) {
      const s = splitSection(r.label);
      cat1 = s.cat1;
      cat2 = s.cat2;
      continue;
    }
    const tone = r.tone ?? '';
    const big = TONE_LABELS[tone] ?? cat1;
    // 小計・合計・率の行は中分類を空にする(2026-09-11 の見本)
    const mid = r.summary ? '' : big === cat1 ? cat2 : '';
    lines.push({ row: r, cat1: big, cat2: mid, child: false, summary: !!r.summary });
    if ((r.children?.length ?? 0) > 0 && open.has(r.key)) {
      for (const c of r.children!) lines.push({ row: c, cat1: big, cat2: mid, child: true, summary: false });
    }
  }
  // 2) 同じ 大分類・中分類 が続く分はセルを縦につなぐ(シートの結合セルと同じ。0=前の行につながる)
  const span1 = new Array(lines.length).fill(0) as number[];
  const span2 = new Array(lines.length).fill(0) as number[];
  for (let i = 0; i < lines.length; ) {
    let j = i;
    while (j < lines.length && lines[j]!.cat1 === lines[i]!.cat1) j++;
    span1[i] = j - i;
    i = j;
  }
  for (let i = 0; i < lines.length; ) {
    let j = i;
    while (j < lines.length && lines[j]!.cat1 === lines[i]!.cat1 && lines[j]!.cat2 === lines[i]!.cat2 && lines[j]!.summary === lines[i]!.summary) j++;
    span2[i] = j - i;
    i = j;
  }

  // 列幅は固定(会社の列はすべて同じ幅。2026-09-11 指示)。大分類・中分類は縦書きで細く
  const W = { cat1: 30, cat2: 30, item: 220, company: 116, total: 128 };
  // 左3列(大分類・中分類・項目)は横スクロールしても固定する(設計書 §4-4)
  const L = { cat1: 0, cat2: W.cat1, item: W.cat1 + W.cat2 };
  const width = W.cat1 + W.cat2 + W.item + cols.length * W.company + W.total;

  const body = lines.map((ln, i) => {
    const r = ln.row;
    const has = !ln.child && (r.children?.length ?? 0) > 0;
    const isOpen = open.has(r.key);
    const help = ln.child ? null : helpOf(r);
    const cls = [has ? 'is-clickable' : '', span1[i]! > 0 && i > 0 ? 'group-start' : '', ln.summary ? 'is-subtotal' : '', r.bold && !ln.summary ? 'lv-0' : '', ln.child ? 'lv-2' : ''].filter(Boolean).join(' ');
    return (
      <tr key={r.key} className={cls || undefined} onClick={has ? () => toggle(r.key) : undefined}>
        {span1[i]! > 0 && (
          <td className="pl-cat1 is-sticky" style={{ left: L.cat1 }} rowSpan={span1[i]}>
            {ln.cat1}
          </td>
        )}
        {span2[i]! > 0 && (
          <td className="pl-cat2 is-sticky" style={{ left: L.cat2 }} rowSpan={span2[i]}>
            {ln.cat2}
          </td>
        )}
        <td className="pl-label is-sticky is-sticky-edge" style={{ left: L.item }}>
          <span className={`tree-cell${ln.child ? ' lv-1' : ''}`}>
            {has ? (
              <button
                type="button"
                className="report-toggle"
                aria-expanded={isOpen}
                aria-label={`${r.label}のアポインター別を${isOpen ? '閉じる' : '開く'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(r.key);
                }}
              >
                <IconChevronRight size={14} />
              </button>
            ) : ln.child ? (
              <span className="cell-na" aria-hidden="true">
                └
              </span>
            ) : (
              <span className="report-toggle-space" />
            )}
            <span className="tree-label">{help ? <HelpTip help={help}>{r.label}</HelpTip> : r.label}</span>
          </span>
        </td>
        {valueCells(r)}
      </tr>
    );
  });


  return (
    <div className="report-scroll">
      {/* 幅は CSS 変数で渡す(スマホの縦積みでは幅の指定を外すため) */}
      <table className={`report-table pl-sheet${compact ? ' is-compact' : ''}`} style={{ '--pl-w': `${width}px` } as React.CSSProperties}>
        <caption className="sr-only">PL シート版(金額の単位 {MONEY_UNIT_LABELS[unit]})</caption>
        <colgroup>
          <col style={{ width: W.cat1 }} />
          <col style={{ width: W.cat2 }} />
          <col style={{ width: W.item }} />
          {cols.map((c) => (
            <col key={c.id} style={{ width: W.company }} />
          ))}
          <col style={{ width: W.total }} />
        </colgroup>
        <thead>
          <tr>
            {/* 大分類・中分類の見出しは出さない(現行シートも空欄。2026-09-11 指示) */}
            <th className="pl-cat is-sticky" style={{ left: 0 }} aria-label="大分類" />
            <th className="pl-cat is-sticky" style={{ left: 30 }} aria-label="中分類" />
            <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 60 }}>項目{unit === 'yen' ? '' : `(${MONEY_UNIT_LABELS[unit]})`}</th>
            {cols.map((c) => (
              <th key={c.id} scope="col" className="is-num pl-company">
                {c.name}
              </th>
            ))}
            <th scope="col" className="is-num col-total">
              合計
            </th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
